import { DOCUMENT } from '@angular/common';
import {
    Component,
    Inject,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserService } from 'app/core/user/user.service';
import { SystemUser, User } from 'app/core/user/user.types';
import { UserComponent } from 'app/layout/common/user/user.component';
import { filter, Subject, takeUntil } from 'rxjs';
import { appVersion as APP_VERSION } from '../../../../../version';

@Component({
    selector: 'eastel-2-layout',
    templateUrl: './eastel-2.component.html',
    styleUrls: ['./eastel-2.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        UserComponent,
        MatIconModule,
        MatButtonModule,
        MatTooltipModule,
        RouterOutlet,
    ],
})
export class Eastel2LayoutComponent implements OnInit, OnDestroy {
    isScreenSmall: boolean;
    navigation: Navigation;
    user: User;
    systemUser: SystemUser;
    pageTitle = 'Dashboard';
    pageSection = 'Workspace';
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    appVersion: string = APP_VERSION;

    constructor(
        @Inject(DOCUMENT) private _document: Document,
        private _renderer: Renderer2,
        private _router: Router,
        private _navigationService: NavigationService,
        private _userService: UserService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService
    ) {}

    get currentYear(): number {
        return new Date().getFullYear();
    }

    ngOnInit(): void {
        this._renderer.addClass(this._document.body, 'eastel-business-portal');

        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to the user service
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;
            });

        this._userService.currentSystemUser$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((systemUser: SystemUser) => {
                this.systemUser = systemUser;
            });

        this._router.events
            .pipe(
                filter(
                    (event): event is NavigationEnd =>
                        event instanceof NavigationEnd
                ),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => this.updatePageContext());
        this.updatePageContext();

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                this.isScreenSmall = !matchingAliases.includes('md');
            });
    }

    ngOnDestroy(): void {
        this._renderer.removeClass(
            this._document.body,
            'eastel-business-portal'
        );
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    toggleNavigation(name: string): void {
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name
            );

        if (navigation) {
            navigation.toggle();
        }
    }

    get displayName(): string {
        const name = [this.systemUser?.firstName, this.systemUser?.lastName]
            .filter(Boolean)
            .join(' ');
        return name || this.systemUser?.userName || 'BOSS User';
    }

    get roleLabel(): string {
        return this.systemUser?.role
            ? this.systemUser.role.replaceAll('_', ' ')
            : 'Team member';
    }

    private updatePageContext(): void {
        const segments = this._router.url
            .split('?')[0]
            .split('/')
            .filter(Boolean);
        const section = segments[0] || 'dashboard';
        const page = segments.at(-1) || section;

        this.pageSection = this.toTitle(section);
        this.pageTitle = this.toTitle(page);
    }

    private toTitle(value: string): string {
        const aliases: Record<string, string> = {
            analytics: 'Analytics',
            updates: "What's New",
            'view-subscriber': 'View Subscriber',
            'failed-operations': 'Failed Operations',
            'product-sales-summary': 'Product Sales Summary',
            'end-of-day-closing': 'End of Day Closing',
            'dynamic-report': 'Dynamic Report',
        };

        return (
            aliases[value] ||
            value
                .split('-')
                .map(
                    (part) =>
                        part.charAt(0).toUpperCase() +
                        part.slice(1).toLowerCase()
                )
                .join(' ')
        );
    }
}
